import { collection, doc, getDoc, query, where, getDocs, Timestamp } from 'firebase/firestore';
import { useApi } from '../services/api';
import { useMemo } from 'react';
import { useFirebase } from '../contexts/FirebaseContext';
import dayjs from 'dayjs';

// Simple cache for game data
const gameCache = new Map();
const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

const GameUtils = (db) => {
  const { post } = useApi();

  const clearUserGamesCache = (userId) => {
    const cacheKey = `user_games_${userId}`;
    gameCache.delete(cacheKey);
  };

  const fetchGameById = async (gameId, includeInvitations = false) => {
    try {
      const gameRef = doc(db, 'games', gameId);
      const gameSnap = await getDoc(gameRef);
      if (gameSnap.exists()) {
        const gameData = gameSnap.data();
        if (includeInvitations) {
          gameData.invitations = await loadInvitations(gameId);
        }
        return { ...gameData, id: gameId };
      }
      return null;
    } catch (error) {
      console.error('Error fetching game: ', error);
      throw error;
    }
  };

  const fetchGameByShareCode = async (shareCode) => {
    try {
      const gamesRef = collection(db, 'games');
      const q = query(gamesRef, where('shareCode', '==', shareCode));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const gameDoc = querySnapshot.docs[0];
        const gameData = { ...gameDoc.data(), id: gameDoc.id };
        gameData.invitations = await loadInvitations(gameDoc.id);
        return gameData;
      }
      return null;
    } catch (error) {
      console.error('Error fetching game: ', error);
      throw error;
    }
  };

  const sortGamesByCreatedAt = (games) => {
    return games.sort((a, b) => (a.createdAt?.toMillis() || 0) - (b.createdAt?.toMillis() || 0));
  };

  const sortGamesByDateOldestFirst = (games) => {
    return games.sort((a, b) => (a.date?.toMillis() || 0) - (b.date?.toMillis() || 0));
  };

  const sortGamesByDateNewestFirst = (games) => {
    return games.sort((a, b) => (b.date?.toMillis() || 0) - (a.date?.toMillis() || 0));
  };

  const fetchAllGamesForUser = async (userId) => {
    // Check cache first
    const cacheKey = `user_games_${userId}`;
    const cached = gameCache.get(cacheKey);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    try {
      // Use the new backend API endpoint to efficiently fetch all user games
      // const response = await fetch(`/api/games/user/${userId}?includeCompleted=true&limit=50`);
      const baseUrl = `https://us-central1-${process.env.REACT_APP_FIREBASE_PROJECT_ID}.cloudfunctions.net/api`;
      const response = await fetch(
        `${baseUrl}/games/user/${userId}?includeCompleted=true&limit=50`
      );

      console.debug('GameUtils: API response status:', response.status);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();

      console.debug('GameUtils: API result:', result);

      if (!result.success) {
        throw new Error(result.message || 'Failed to fetch games');
      }

      const allGames = result.data.games || [];
      console.debug('GameUtils: Raw games from API:', allGames.length);

      // Convert Firestore timestamps from the API response to Firebase Timestamp objects
      const gamesWithConvertedDates = allGames.map((game) => ({
        ...game,
        date: game.date ? Timestamp.fromDate(new Date(game.date._seconds * 1000)) : null,
        createdAt: game.createdAt
          ? Timestamp.fromDate(new Date(game.createdAt._seconds * 1000))
          : null,
        dateTimeOptions:
          game.dateTimeOptions?.map((option) => ({
            ...option,
            option: option.option
              ? Timestamp.fromDate(new Date(option.option._seconds * 1000))
              : null,
          })) || [],
      }));

      console.debug('GameUtils: Games after date conversion:', gamesWithConvertedDates.length);

      // Process games into categories using the same logic
      const pendingGames = sortGamesByCreatedAt(
        gamesWithConvertedDates.filter(
          (game) => game?.date === null && game?.status !== 'completed'
        )
      );

      const scheduledGames = sortGamesByDateOldestFirst(
        gamesWithConvertedDates.filter((game) => {
          if (!game?.date || game?.status === 'completed') {
            return false;
          }

          const gameDate = dayjs(game.date.toDate());
          const now = dayjs();
          const gameStartOfDay = gameDate.startOf('day');
          const nowStartOfDay = now.startOf('day');
          const isIncluded =
            gameStartOfDay.isSame(nowStartOfDay) || gameStartOfDay.isAfter(nowStartOfDay);

          return isIncluded;
        })
      );

      const scheduledAndPendingGames = [...scheduledGames, ...pendingGames];

      // myGames should include all games where user is organizer (pending, scheduled, confirmed)
      // Not just scheduled and pending
      const myGames = gamesWithConvertedDates.filter((game) => {
        // Must be created by user
        if (game?.createdBy !== userId) return false;
        
        // Exclude completed/past games
        if (game?.status === 'completed') return false;
        
        // Must be future game or pending (no date yet)
        if (game?.date) {
          const gameDate = dayjs(game.date.toDate());
          if (!gameDate.isAfter(dayjs())) return false;
        }
        
        return true;
      });

      const gamesYouAreInvitedTo = scheduledAndPendingGames.filter((game) => {
        // Check if user is invited
        const isInvited = game?.invitations?.some(
          (invitation) => invitation?.user === userId || invitation?.user === game?.createdBy
        );
        
        if (!isInvited) return false;
        
        // Scenario 6: Filter out games where user responded but wasn't selected
        // If game is finalised (has matches), check if user is in any match
        if (game?.matches && game.matches.length > 0) {
          const userIsInAMatch = game.matches.some((match) =>
            match.players?.some(
              (player) => player.user === userId || player.userId === userId || player.uid === userId
            )
          );
          // If user is NOT in any match, hide this game
          if (!userIsInAMatch) return false;
        }
        
        return true;
      });

      // Past games: Show if (past date + matches) OR (scores finalized)
      // But ONLY games where current user actually played in a match
      const previousGames = sortGamesByDateNewestFirst(
        gamesWithConvertedDates.filter((game) => {
          // Must have matches
          if (!game?.matches?.length) return false;
          
          // Show if: past date OR scores are finalized
          const isPastDate = game?.date && game.date.toDate() < new Date();
          const scoresFinalized = game?.allScoresFinalised === true;
          
          if (!isPastDate && !scoresFinalized) return false;
          
          // CRITICAL: Only show if current user actually played in a match
          const userPlayedInMatch = game.matches.some((match) =>
            match.players?.some((player) => 
              player.user === userId || player.userId === userId || player.uid === userId
            )
          );
          
          return userPlayedInMatch;
        })
      );

      // console.log('previousGames', previousGames);

      // previousGamesIPlayedIn is now the same as previousGames since we filter above
      const previousGamesIPlayedIn = previousGames;
      // console.log('previousGamesIPlayedIn', previousGamesIPlayedIn);
      const confirmedGamesWhereIAmAPlayer = sortGamesByDateNewestFirst(gamesWithConvertedDates)
        ?.filter((game) => {
          const debugInfo = {
            gameId: game.id,
            gameName: game.name,
            hasMatches: !!game?.matches?.length,
            matchCount: game?.matches?.length || 0,
            hasDate: !!game?.date,
            gameDate: game?.date ? dayjs(game.date.toDate()).format('YYYY-MM-DD HH:mm') : null,
            isFutureDate: game?.date ? dayjs(game.date.toDate()).isAfter(dayjs()) : false,
            status: game.status,
            isConfirmed: game.status === 'confirmed',
          };

          // Must have matches
          if (!game?.matches?.length) {
            console.log('❌ Confirmed filter: No matches', debugInfo);
            return false;
          }

          // Must have a future date
          if (!game?.date) {
            console.log('❌ Confirmed filter: No date', debugInfo);
            return false;
          }
          const gameDate = dayjs(game.date.toDate());
          if (!gameDate.isAfter(dayjs())) {
            console.log('❌ Confirmed filter: Date not in future', debugInfo);
            return false;
          }

          // Must have status confirmed
          if (game.status !== 'confirmed') {
            console.log('❌ Confirmed filter: Status not confirmed', debugInfo);
            return false;
          }

          // Must have matches without scores entered yet
          // Check if any player in any match has meaningful scores (not just default zeros)
          const hasMatchesWithScores = game.matches.some((match) => 
            match.players?.some(player => {
              const hasWinner = player.didWin === true || player.win === true;
              const hasGrossScore = player.grossScore && player.grossScore > 0;
              const hasNetScore = player.netScore && player.netScore > 0;
              return hasWinner || hasGrossScore || hasNetScore;
            })
          );
          // If scores exist, don't show in confirmed games section
          if (hasMatchesWithScores) {
            console.log('❌ Confirmed filter: Scores already exist', debugInfo);
            return false;
          }

          // User must be in at least one match
          const userIsInAMatch = game.matches.some((match) =>
            match.players?.some(
              (player) => player.user === userId || player.userId === userId || player.uid === userId
            )
          );
          if (!userIsInAMatch) {
            console.log('❌ Confirmed filter: User not in any match', { ...debugInfo, userId, matches: game.matches });
            return false;
          }

          console.log('✅ Confirmed filter: PASSED', debugInfo);
          return true;
        });

      console.log('confirmedGamesWhereIAmAPlayer', confirmedGamesWhereIAmAPlayer);

      console.debug('GameUtils: Categories:', {
        pending: pendingGames.length,
        scheduled: scheduledGames.length,
        previous: previousGames.length,
        previousGamesIPlayedIn: previousGamesIPlayedIn.length,
        confirmedGamesWhereIAmAPlayer: confirmedGamesWhereIAmAPlayer.length,
      });

      const pendingGamesNoGamesInPast = pendingGames.filter((game) => {
        // If there are no dateTimeOptions, include the game (it's still pending)
        if (!game?.dateTimeOptions || game.dateTimeOptions.length === 0) {
          return true;
        }

        // Find the latest time in the datetimeoptions using dayjs
        const latestDateInPoll = game.dateTimeOptions.reduce((max, option) => {
          const optionTime = dayjs(option.option.toDate());
          const maxTime = dayjs(max);
          return optionTime.isAfter(maxTime) ? optionTime : max;
        }, dayjs(0));

        // Only include games where the latest option is in the future
        return latestDateInPoll.isAfter(dayjs());
      });

      const processedResult = {
        pendingGames: pendingGamesNoGamesInPast,
        scheduledGames,
        previousGames,
        myGames,
        gamesYouAreInvitedTo,
        previousGamesIPlayedIn,
        confirmedGamesWhereIAmAPlayer,
      };

      console.log('GameUtils: Final result:', {
        pending: processedResult.pendingGames.length,
        scheduled: processedResult.scheduledGames.length,
        previous: processedResult.previousGames.length,
        myGames: processedResult.myGames.length,
        gamesYouAreInvitedTo: processedResult.gamesYouAreInvitedTo.length,
        previousGamesIPlayedIn: processedResult.previousGamesIPlayedIn.length,
        confirmedGamesWhereIAmAPlayer: processedResult.confirmedGamesWhereIAmAPlayer.length,
      });

      // Cache the result
      gameCache.set(cacheKey, {
        data: processedResult,
        timestamp: Date.now(),
      });

      return processedResult;
    } catch (error) {
      console.error('Error fetching games via API:', error);
      throw error;
    }
  };

  const loadInvitations = async (gameId) => {
    try {
      const invitationsRef = collection(db, 'games', gameId, 'invitations');
      const querySnapshot = await getDocs(invitationsRef);
      const invitationsWithDetails = await Promise.all(
        querySnapshot.docs.map(async (invitationDoc) => {
          const invitation = invitationDoc.data();
          const userRef = doc(db, invitation.user.path);
          const userSnap = await getDoc(userRef);
          const user = userSnap.data();

          let rsvp = 'pending';
          if (
            invitation.responded &&
            invitation.preferredDates &&
            invitation.preferredDates.length > 0
          ) {
            rsvp = 'accepted';
          } else if (
            invitation.responded &&
            invitation.preferredDates &&
            invitation.preferredDates.length === 0
          ) {
            rsvp = 'declined';
          }

          return { ...invitation, id: invitationDoc.id, user, rsvp };
        })
      );
      return invitationsWithDetails;
    } catch (error) {
      console.error('Error fetching invited users: ', error);
      throw error;
    }
  };

  const acceptInvitation = async (gameId, userId, dateTimeOptions) => {
    try {
      await post('/invitation/accept-invitation', {
        gameId,
        userId,
        dateTimeOptions,
      });
    } catch (error) {
      console.error('Error accepting invitation: ', error);
      throw error;
    }
  };

  // TODO: Refactor fetchAllPreviousPlayers to be properly testable and add comprehensive tests
  // Currently this function is not easily testable due to its Firebase dependencies and complex internal logic
  // Recommendations for refactoring:
  // 1. Extract Firebase queries into separate, injectable functions (e.g., fetchGamesByCreator, fetchAllGames)
  // 2. Split logic into smaller, pure functions (e.g., extractUsersFromGames, deduplicateUsers)
  // 3. Consider dependency injection pattern to allow mocking of Firebase operations
  // 4. Move to a service layer that can be properly unit tested with mocked dependencies
  const fetchAllPreviousPlayers = async (currentUserId) => {
    try {
      const userRef = doc(db, 'users', currentUserId);
      const gamesRef = collection(db, 'games');

      // Query games where the user is the creator
      const q = query(gamesRef, where('createdBy', '==', userRef));
      const snapshot = await getDocs(q);

      const allGamesUserIsCreatorOf = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      const allInvitedUsers = allGamesUserIsCreatorOf
        .filter((game) => game?.invitations)
        .map((game) => game?.invitations)
        .flat()
        .map((invite) => invite.user);

      const allGamesQuery = query(gamesRef);
      const allGamesSnapshot = await getDocs(allGamesQuery);
      const allGames = allGamesSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      const gamesWhereUserIsInvited = allGames.filter((game) =>
        game?.invitations?.find((invite) => invite.user === currentUserId)
      );

      // Get all invitees from games where user is invited
      const allInviteesFromGamesWhereUserIsInvited = gamesWhereUserIsInvited
        .map((game) => game.invitations)
        .flat()
        .map((invite) => invite.user);

      // Get game creators from games where user is invited (fixing the missing players issue)
      const gameCreatorsWhereUserIsInvited = gamesWhereUserIsInvited
        .map((game) => game.createdBy)
        .filter((creatorId) => creatorId && creatorId !== currentUserId);

      const allUsers = [
        ...allInvitedUsers,
        ...allInviteesFromGamesWhereUserIsInvited,
        ...gameCreatorsWhereUserIsInvited,
      ];
      // Remove duplicates and filter out current user (don't show yourself in contact/player lists)
      const allUsersDuplicatesRemoved = [...new Set(allUsers)].filter(
        (userId) => userId !== currentUserId
      );
      const _users = await Promise.all(
        allUsersDuplicatesRemoved.map(async (userId) => {
          const userRef = doc(db, 'users', userId);
          const userDoc = await getDoc(userRef);
          const userData = userDoc.exists ? userDoc.data() : {};
          return {
            ...userData,
            photoURL: userData?.profileImage || null,
          };
        })
      );
      // Filter out invalid user data to ensure clean leaderboard
      return _users.filter((user) => user.uid && user.username);
    } catch (error) {
      console.error('Error fetching games:', error);
    }
  };

  const calculateWaglScore = async (userId) => {
    try {
      // Get leaderboard data from on-demand rating API
      const response = await post('/api/rating/leaderboard');

      if (!response.success) {
        console.error('Failed to fetch leaderboard:', response.error);
        return {
          rank: 0,
          totalPlayers: 0,
          leaderboard: [],
          waglScore: 50, // Default rating
        };
      }

      const leaderboard = response.data.leaderboard || [];
      const userRank = leaderboard.findIndex((player) => player.uid === userId) + 1;
      const userRating = leaderboard.find((player) => player.uid === userId)?.currentRating || 50;

      // Format leaderboard for compatibility with existing UI
      const formattedLeaderboard = leaderboard.map((player, index) => ({
        username: player.username,
        rank: index + 1,
        waglScore: player.currentRating, // Use on-demand rating as waglScore
        uid: player.uid,
      }));

      return {
        rank: userRank || 0,
        totalPlayers: leaderboard.length,
        leaderboard: formattedLeaderboard,
        waglScore: userRating, // Use on-demand rating
      };
    } catch (error) {
      console.error('Error calculating WAGL score:', error);
      return {
        rank: 0,
        totalPlayers: 0,
        leaderboard: [],
        waglScore: 50, // Default rating
      };
    }
  };

  const getLatestWaglScoreRecord = async (userId) => {
    try {
      // Get user rating data from on-demand API
      const response = await post(`/api/rating/user/${userId}`);

      if (!response.success) {
        console.error('Failed to fetch user rating:', response.error);
        return {
          latestScore: 50, // Default rating
          previousScore: 50,
        };
      }

      const ratingData = response.data;
      return {
        latestScore: ratingData.currentRating || 50,
        previousScore: ratingData.previousRating || 50, // This might need rating history API
      };
    } catch (error) {
      console.error('Error getting latest WAGL score record:', error);
      return {
        latestScore: 50, // Default rating
        previousScore: 50,
      };
    }
  };

  const createCombinedInvitationsPlayers = (gameData) => {
    const _invitations =
      gameData?.invitations?.map((inv) => ({ ...inv, type: 'invitation' })) || [];
    // const _players = gameData?.acceptedBy?.map((player) => ({ ...player, type: 'player' })) || [];

    const _declinedPlayers =
      gameData?.declinedBy?.map((player) => ({
        user: player,
        type: 'declined',
        // user: player.uid,
      })) || [];

    let _players = [];
    gameData?.dateTimeOptions?.forEach((option) => {
      option.acceptedBy?.forEach((acceptedBy) => {
        _players.push({ ...acceptedBy, type: 'player', user: acceptedBy.uid });
      });
    });

    let deduped = [];
    const seenUsers = new Set();

    // First, add all players to the result and mark them as seen
    for (const entry of _players || []) {
      if (entry.user) {
        seenUsers.add(entry.user);
        deduped.push(entry);
      }
    }

    // Then, add invitations **only if** the user hasn't already been seen as a player
    for (const entry of _invitations || []) {
      if (entry.user && !seenUsers.has(entry.user)) {
        deduped.push(entry);
      }
    }

    // Then, add declines **only if** the user hasn't already been seen as a player
    for (const entry of _declinedPlayers || []) {
      if (entry.user && !seenUsers.has(entry.user)) {
        deduped.push(entry);
      }
    }

    deduped = deduped.filter(
      (entry, index, self) => index === self.findIndex((e) => e.user === entry.user)
    );
    return deduped;
  };

  const getAllUserDetailsByUserIds = async (userIds) => {
    // Filter out any undefined, null, or empty userIds
    const validUserIds = userIds.filter((uid) => uid && uid.trim && uid.trim() !== '');

    const users = await Promise.all(
      validUserIds.map(async (uid) => {
        const userRef = doc(db, 'users', uid);
        const userDoc = await getDoc(userRef);
        const userData = userDoc.exists ? userDoc.data() : {};
        const userName = userData?.userName ? userData?.userName : userData?.username || '';
        return {
          ...userData,
          user: uid,
          photoURL: userData?.profileImage || null,
          userName: userName,
          initial: userName && userName.length > 0 ? userName.charAt(0).toUpperCase() : '',
        };
      })
    );
    return users;
  };

  const getAllGamesRecordsForUser = async (userId) => {
    const gamesRef = collection(db, 'games');
    // Get ALL games, then filter for ones where user participated
    const snapshot = await getDocs(gamesRef);

    const gamesWithDate = snapshot.docs
      .filter((doc) => doc.data().date)
      .map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }))
      // Only include games where the user actually participated
      .filter((game) => game?.players?.some((player) => player?.user === userId));

    // get all matches from games
    const matches = gamesWithDate
      .flatMap((game) => game.matches || [])
      .filter((match) => match.format === 'match')
      .filter((match) => match.players?.some((player) => player?.user === userId));

    const matchResults = matches.reduce(
      (acc, match) => {
        const userPlayer = match.players.find((player) => player?.user === userId);

        if (!userPlayer || userPlayer.grossScore == null) return acc;
        const opponent = match.players.find((player) => player?.user !== userId);
        if (!opponent || opponent.grossScore == null) return acc;

        if (userPlayer.grossScore > opponent.grossScore) {
          acc.wins++;
        } else if (userPlayer.grossScore < opponent.grossScore) {
          acc.losses++;
        } else {
          acc.ties++;
        }
        return acc;
      },
      { wins: 0, losses: 0, ties: 0 }
    );

    // Count unique players the user has played with
    const uniquePartners = new Set();
    gamesWithDate.forEach((game) => {
      if (game?.players?.some((player) => player?.user === userId)) {
        game.players.forEach((player) => {
          if (player?.user && player.user !== userId) {
            uniquePartners.add(player.user);
          }
        });
      }
    });
    const playersPlayedWith = uniquePartners.size;

    const totalGames = matchResults.wins + matchResults.losses + matchResults.ties;
    const winLossPercentage = totalGames > 0 ? (matchResults.wins / totalGames) * 100 : 0;

    return {
      gamesCount: gamesWithDate.length,
      wins: matchResults.wins,
      losses: matchResults.losses,
      playersPlayedWith,
      winLossPercentage,
    };
  };

  const getAllAvailableGameData = async () => {
    const gamesRef = collection(db, 'games');
    const q = query(gamesRef, where('active', '==', true), where('visibility', '==', 'Open'));
    const snapshot = await getDocs(q);
    let allAvailableGames = [];
    allAvailableGames = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
      createdBy: doc.data().createdBy,
    }));
    allAvailableGames = allAvailableGames.filter((game) => {
      if (game.date) {
        return game.date.toMillis() > Timestamp.now().toMillis();
      }
      return true;
    });
    const allInvitedUsers = allAvailableGames.map((game) => game.invitations);
    return {
      games: allAvailableGames,
      invitedUsers: allInvitedUsers,
    };
  };

  const getLatestWaglRatingRecord = async (userId) => {
    try {
      // Get user rating data from on-demand API
      const response = await post(`/api/rating/user/${userId}`);

      if (!response.success) {
        console.error('Failed to fetch user rating:', response.error);
        return null;
      }

      const ratingData = response.data;
      return {
        newRating: ratingData.currentRating || 50,
        previousRating: ratingData.previousRating || 50,
        userId: userId,
        createdAt: new Date(), // Current timestamp since it's computed on-demand
      };
    } catch (error) {
      console.error('Error getting latest WAGL rating record:', error);
      return null;
    }
  };

  const getAllPlayersForGame = async (gameId) => {
    const gameRef = doc(db, 'games', gameId);
    const gameSnap = await getDoc(gameRef);
    const gameData = gameSnap.data();
    const players = gameData?.players;
    const users = await getAllUserDetailsByUserIds(players.map((player) => player.user));
    const userWithPlayerData = users.map((user) => {
      const player = players.find((p) => p.user === user.uid);
      return { ...user, paid: player.paid, costs: player.costs };
    });
    return userWithPlayerData;
  };

  const updateGreenFees = async ({ gameId, players, cost, splitCosts }) => {
    console.log('updateGreenFees', { gameId, players, cost, splitCosts });
    try {
      await post('/game/update-green-fees', { gameId, players, cost, splitCosts });
    } catch (error) {
      console.error('Error updating green fees:', error);
      throw error;
    }
  };

  const utils = useMemo(
    () => ({
      fetchGameByShareCode,
      fetchGameById,
      loadInvitations,
      acceptInvitation,
      fetchAllPreviousPlayers,
      calculateWaglScore,
      getLatestWaglScoreRecord,
      createCombinedInvitationsPlayers,
      getAllUserDetailsByUserIds,
      getAllGamesRecordsForUser,
      getAllAvailableGameData,
      getLatestWaglRatingRecord,
      fetchAllGamesForUser,
      getAllPlayersForGame,
      updateGreenFees,
      clearUserGamesCache,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  ); // Empty dependency array since these functions don't depend on any props or state

  return utils;
};

export function useGameUtils() {
  const { db } = useFirebase();
  return GameUtils(db);
}
